import type { TUser } from '@roko/ui/types/common'
import type { StateCreator } from 'zustand'
import { create } from 'zustand'
import type { PersistOptions } from 'zustand/middleware'
import { persist } from 'zustand/middleware'

import { getEntraAuthorizeUrl, getEntraProfileFromTokens } from '@/auth/entraConfig'
import { clearTokens, getAccessToken, hasValidEntraAccessToken, setAccessToken } from '@/data/api/token'

type TInitialState = {
  user: TUser | null
  isAuthenticated: boolean
}

const INITIAL_STATE: TInitialState = {
  user: null,
  isAuthenticated: false,
}

interface IAuthStore extends TInitialState {
  completeEntraLogin: (params: { accessToken: string; userName: string; email?: string }) => void
  logout: () => Promise<void>
}

type TAuthPersist = (
  config: StateCreator<IAuthStore>,
  options: PersistOptions<IAuthStore, Pick<IAuthStore, 'user' | 'isAuthenticated'>>
) => StateCreator<IAuthStore>

const userFromAccessToken = (): TUser | null => {
  const accessToken = getAccessToken()
  if (!accessToken || !hasValidEntraAccessToken()) {
    return null
  }

  const profile = getEntraProfileFromTokens({ access_token: accessToken })
  return { userName: profile.userName, email: profile.email }
}

const syncSessionFromToken = (): TInitialState => {
  const user = userFromAccessToken()
  if (!user) {
    return INITIAL_STATE
  }
  return { user, isAuthenticated: true }
}

export const useAuthStore = create<IAuthStore>()(
  (persist as TAuthPersist)(
    (set) => ({
      ...syncSessionFromToken(),
      completeEntraLogin: ({ accessToken, userName, email }) => {
        setAccessToken(accessToken)
        set({ user: { userName, email }, isAuthenticated: true })
      },
      logout: async () => {
        clearTokens()
        set(INITIAL_STATE)
        window.location.assign(await getEntraAuthorizeUrl())
      },
    }),
    {
      name: 'auth-storage-admin',
      partialize: (state) => ({ user: state.user, isAuthenticated: state.isAuthenticated }),
      onRehydrateStorage: () => () => {
        if (hasValidEntraAccessToken()) {
          useAuthStore.setState(syncSessionFromToken())
          return
        }
        clearTokens()
        useAuthStore.setState(INITIAL_STATE)
      },
    }
  )
)
